"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FileJson, Copy, Check, Wand2, AlertCircle } from "lucide-react"
import { toast } from "sonner"

interface SchemaOptions {
  draft: "draft-04" | "draft-06" | "draft-07" | "2019-09" | "2020-12"
  required: boolean
  additionalProperties: boolean
  examples: boolean
  descriptions: boolean
}

function getJsonType(value: unknown): string {
  if (value === null) return "null"
  if (Array.isArray(value)) return "array"
  return typeof value
}

function inferSchema(
  value: unknown,
  options: SchemaOptions,
  key?: string
): Record<string, unknown> {
  const type = getJsonType(value)

  const schema: Record<string, unknown> = {}

  if (options.descriptions && key) {
    schema.description = `The ${key.replace(/([A-Z])/g, " $1").toLowerCase()} field`
  }

  switch (type) {
    case "string":
      schema.type = "string"
      if (options.examples) {
        schema.examples = [value]
      }
      // Detect common formats
      const strValue = value as string
      if (/^\d{4}-\d{2}-\d{2}$/.test(strValue)) {
        schema.format = "date"
      } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(strValue)) {
        schema.format = "date-time"
      } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strValue)) {
        schema.format = "email"
      } else if (/^https?:\/\//.test(strValue)) {
        schema.format = "uri"
      } else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(strValue)) {
        schema.format = "uuid"
      } else if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(strValue)) {
        schema.format = "ipv4"
      }
      break

    case "number":
      schema.type = Number.isInteger(value) ? "integer" : "number"
      if (options.examples) {
        schema.examples = [value]
      }
      break

    case "boolean":
      schema.type = "boolean"
      if (options.examples) {
        schema.examples = [value]
      }
      break

    case "null":
      schema.type = "null"
      break

    case "array":
      schema.type = "array"
      const arr = value as unknown[]
      if (arr.length > 0) {
        // Analyze all items to find common schema
        const itemSchemas = arr.map((item) => inferSchema(item, options))
        // Use first item's schema (could be enhanced to merge schemas)
        schema.items = itemSchemas[0]
      } else {
        schema.items = {}
      }
      if (options.examples && arr.length > 0) {
        schema.examples = [arr.slice(0, 2)]
      }
      break

    case "object":
      schema.type = "object"
      const obj = value as Record<string, unknown>
      const properties: Record<string, unknown> = {}
      const requiredProps: string[] = []

      for (const [k, v] of Object.entries(obj)) {
        properties[k] = inferSchema(v, options, k)
        if (options.required && v !== null && v !== undefined) {
          requiredProps.push(k)
        }
      }

      if (Object.keys(properties).length > 0) {
        schema.properties = properties
      }

      if (options.required && requiredProps.length > 0) {
        schema.required = requiredProps
      }

      if (!options.additionalProperties) {
        schema.additionalProperties = false
      }
      break
  }

  return schema
}

function generateSchema(json: unknown, options: SchemaOptions): Record<string, unknown> {
  const draftUrls: Record<string, string> = {
    "draft-04": "http://json-schema.org/draft-04/schema#",
    "draft-06": "http://json-schema.org/draft-06/schema#",
    "draft-07": "http://json-schema.org/draft-07/schema#",
    "2019-09": "https://json-schema.org/draft/2019-09/schema",
    "2020-12": "https://json-schema.org/draft/2020-12/schema",
  }

  const schema = inferSchema(json, options)

  return {
    $schema: draftUrls[options.draft],
    ...schema,
  }
}

export function JsonSchemaGenerator() {
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [options, setOptions] = useState<SchemaOptions>({
    draft: "draft-07",
    required: true,
    additionalProperties: false,
    examples: true,
    descriptions: false,
  })

  const generate = useCallback(() => {
    if (!input.trim()) {
      toast.error("Please enter JSON data")
      return
    }

    setError(null)

    try {
      const json = JSON.parse(input)
      const schema = generateSchema(json, options)
      setOutput(JSON.stringify(schema, null, 2))
      toast.success("Schema generated successfully")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Invalid JSON"
      setError(message)
      toast.error("Invalid JSON input")
    }
  }, [input, options])

  const copy = useCallback(() => {
    if (!output) return
    navigator.clipboard.writeText(output)
    setCopied(true)
    toast.success("Copied to clipboard")
    setTimeout(() => setCopied(false), 2000)
  }, [output])

  const loadSample = useCallback(() => {
    const sample = JSON.stringify(
      {
        id: "550e8400-e29b-41d4-a716-446655440000",
        name: "John Doe",
        email: "john@example.com",
        age: 30,
        isActive: true,
        website: "https://example.com",
        createdAt: "2024-01-15T10:30:00Z",
        tags: ["developer", "designer"],
        address: {
          street: "123 Main St",
          city: "New York",
          zipCode: "10001",
        },
        metadata: null,
      },
      null,
      2
    )
    setInput(sample)
    setError(null)
    setOutput("")
  }, [])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileJson className="h-5 w-5" />
            JSON Schema Generator
          </CardTitle>
          <CardDescription>
            Generate JSON Schema from example JSON data. Automatically detects formats like email, date, UUID, and URI.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Options */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label>Schema Draft</Label>
              <Select
                value={options.draft}
                onValueChange={(v) => setOptions({ ...options, draft: v as SchemaOptions["draft"] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft-04">Draft-04</SelectItem>
                  <SelectItem value="draft-06">Draft-06</SelectItem>
                  <SelectItem value="draft-07">Draft-07 (Recommended)</SelectItem>
                  <SelectItem value="2019-09">2019-09</SelectItem>
                  <SelectItem value="2020-12">2020-12</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="required"
                checked={options.required}
                onCheckedChange={(v) => setOptions({ ...options, required: v })}
              />
              <Label htmlFor="required">Mark fields required</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="additional"
                checked={options.additionalProperties}
                onCheckedChange={(v) => setOptions({ ...options, additionalProperties: v })}
              />
              <Label htmlFor="additional">Allow additional props</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="examples"
                checked={options.examples}
                onCheckedChange={(v) => setOptions({ ...options, examples: v })}
              />
              <Label htmlFor="examples">Include examples</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="descriptions"
                checked={options.descriptions}
                onCheckedChange={(v) => setOptions({ ...options, descriptions: v })}
              />
              <Label htmlFor="descriptions">Auto descriptions</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">JSON Input</CardTitle>
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder='{"name": "John", "age": 30}'
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                setError(null)
              }}
              className="min-h-[300px] font-mono text-sm"
            />
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <Button onClick={generate} className="w-full">
              <Wand2 className="h-4 w-4 mr-2" />
              Generate Schema
            </Button>
          </CardContent>
        </Card>

        {/* Output */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">JSON Schema</CardTitle>
              {output && (
                <Button variant="outline" size="sm" onClick={copy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span className="ml-2">{copied ? "Copied" : "Copy"}</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder="Generated schema will appear here..."
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Detected Formats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-3 text-sm">
            <div className="p-2 rounded bg-muted/50">
              <span className="font-medium">email:</span>{" "}
              <code>user@example.com</code>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <span className="font-medium">date:</span>{" "}
              <code>2024-01-15</code>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <span className="font-medium">date-time:</span>{" "}
              <code>2024-01-15T10:30:00Z</code>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <span className="font-medium">uri:</span>{" "}
              <code>https://example.com</code>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <span className="font-medium">uuid:</span>{" "}
              <code>550e8400-e29b-...</code>
            </div>
            <div className="p-2 rounded bg-muted/50">
              <span className="font-medium">ipv4:</span>{" "}
              <code>192.168.1.1</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
